import { expiresAt, phaseAt, type PingResult, type SessionState, type SessionTimingConfig, shouldKeepAlive } from "./sessionTiming";

const DEFAULT_TICK_MS = 1000;
const DEFAULT_MIN_PING_INTERVAL_MS = 30_000;

export interface SessionMonitorOptions {
  config: SessionTimingConfig;
  /** Epoch ms of the last successful API call. */
  getLastActivityAt: () => number;
  /** Renews the session (GET /api/me). */
  ping: () => Promise<PingResult>;
  onChange: (state: SessionState) => void;
  /** The server confirmed the session is gone. */
  onExpired?: () => void;
  now?: () => number;
  tickMs?: number;
  /** Minimum time between two automatic pings (avoids storms on 429/5xx). */
  minPingIntervalMs?: number;
}

export interface SessionMonitor {
  start: () => void;
  stop: () => void;
  /** Call on real user activity (pointer, keyboard, scroll, tab visible). */
  activity: () => void;
  /** Explicit "Stay signed in": pings regardless of the phase. */
  stayConnected: () => Promise<PingResult>;
  /** Re-reads the clock and the last activity (e.g. after a successful call). */
  evaluate: () => void;
  getState: () => SessionState;
}

/** Runs the session timing against the clock: re-evaluates every tick and pings when needed. */
export function createSessionMonitor(options: SessionMonitorOptions): SessionMonitor {
  const { config, getLastActivityAt, ping, onChange, onExpired } = options;
  const now = options.now ?? Date.now;
  const tickMs = options.tickMs ?? DEFAULT_TICK_MS;
  const minPingIntervalMs = options.minPingIntervalMs ?? DEFAULT_MIN_PING_INTERVAL_MS;

  let timer: ReturnType<typeof setInterval> | undefined;
  let inFlight: Promise<PingResult> | null = null;
  let lastPingAttemptAt = Number.NEGATIVE_INFINITY;
  let stopped = true;
  let state: SessionState = { phase: "active", expiresAt: expiresAt(getLastActivityAt(), config) };

  function emitIfChanged(next: SessionState): void {
    if (next.phase !== state.phase || next.expiresAt !== state.expiresAt) {
      state = next;
      onChange(state);
    }
  }

  function doPing(): Promise<PingResult> {
    if (inFlight) return inFlight;
    lastPingAttemptAt = now();
    inFlight = ping()
      .catch((): PingResult => "unknown")
      .then((result) => {
        inFlight = null;
        if (stopped) return result;
        if (result === "expired") {
          stop();
          onExpired?.();
        } else {
          evaluate();
        }
        return result;
      });
    return inFlight;
  }

  function evaluate(): void {
    if (stopped) return;
    const last = getLastActivityAt();
    const t = now();
    const phase = phaseAt(t, last, config);
    emitIfChanged({ phase, expiresAt: expiresAt(last, config) });
    if (phase === "expired" && !inFlight && t - lastPingAttemptAt >= minPingIntervalMs) {
      // Confirm with the server: another tab may have kept the session alive,
      // or the absolute limit may have ended it.
      void doPing();
    }
  }

  function activity(): void {
    if (stopped) return;
    const t = now();
    if (!shouldKeepAlive(t, getLastActivityAt(), config)) return;
    if (inFlight || t - lastPingAttemptAt < minPingIntervalMs) return;
    void doPing();
  }

  function start(): void {
    if (!stopped) return;
    stopped = false;
    state = { phase: phaseAt(now(), getLastActivityAt(), config), expiresAt: expiresAt(getLastActivityAt(), config) };
    onChange(state);
    timer = setInterval(evaluate, tickMs);
  }

  function stop(): void {
    stopped = true;
    if (timer) clearInterval(timer);
    timer = undefined;
  }

  return {
    start,
    stop,
    activity,
    stayConnected: () => doPing(),
    evaluate,
    getState: () => state,
  };
}
